import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import Sidebar from "./components/Sidebar";
import WorkerList from "./components/WorkerList";
import LoginScreen from "./components/LoginScreen";
import BirthdaysPanel from "./components/BirthdaysPanel";
import BirthdayAlert from "./components/BirthdayAlert";
import ApplicantTab from "./components/ApplicantTab";
import EmployeePanel from "./components/EmployeePanel";
import OnboardingRetention from "./components/OnboardingRetention";
import PbjReport from "./components/PbjReport";
import ChatTab from "./components/ChatTab";
import InterviewCalendar from "./components/InterviewCalendar";
import { fetchOpenShifts, fetchClients, fetchWorkers, fetchDistances } from "./api";
import { SHIFT_PREFS } from "./shiftPrefs";
import banner from "./banner.png";
import "./App.css";

// Empty in dev -> relative "/api" paths, proxied to the local backend (see frontend/.env.*).
const RAILWAY = process.env.REACT_APP_API_BASE || "";

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const user = accounts[0];

  const [mainTab, setMainTab]               = useState("dispatch");
  const [mode, setMode]                     = useState("shifts");
  const [shifts, setShifts]                 = useState([]);
  const [clients, setClients]               = useState([]);
  const [workers, setWorkers]               = useState([]);
  const [staff, setStaff]                   = useState([]);
  const [selectedShift, setSelectedShift]   = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [roleFilter, setRoleFilter]         = useState("all");
  const [shiftPrefFilter, setShiftPrefFilter] = useState("all");
  const [distances, setDistances]           = useState({});
  const [loadingDist, setLoadingDist]       = useState(false);
  const [loadingData, setLoadingData]       = useState(false);
  const [error, setError]                   = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [assignments, setAssignments]       = useState({}); // worker -> assigned shifts over the Open-Shifts window (14d), for double-booking detection
  const [weekStart, setWeekStart]           = useState(null);
  const [weekEnd, setWeekEnd]               = useState(null);
  const [selectedEmployee, setSelectedEmployee]   = useState(null);
  const [clientDistances, setClientDistances]     = useState({});
  const [loadingClientDist, setLoadingClientDist] = useState(false);
  const [refreshing, setRefreshing]               = useState(false);
  const [refreshDone, setRefreshDone]             = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoadingData(true);
    Promise.all([
      fetchOpenShifts(),
      fetchClients(),
      fetchWorkers(),
      fetch(`${RAILWAY}/api/schedule/week`).then(r => r.json()),
      fetch(`${RAILWAY}/api/staff`).then(r => r.json()).catch(() => ({ staff: [] })),
    ])
      .then(([s, c, w, sched, st]) => {
        setShifts(s);
        setClients(c);
        setWorkers(w);
        setStaff(st.staff || []);
        setWeeklySchedule(sched.byWorker || {});
        setAssignments(sched.assignments || {});
        setWeekStart(sched.weekStart || null);
        setWeekEnd(sched.weekEnd || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoadingData(false));
  }, [isAuthenticated]);

  const computeDistances = useCallback(async (address, workerList) => {
    if (!address || !workerList.length) return;
    setLoadingDist(true);
    try {
      const results = await fetchDistances(address, workerList);
      const map = {};
      results.forEach(r => { map[r.workerId] = { miles: r.distanceMiles, minutes: r.durationMinutes }; });
      setDistances(map);
    } catch (e) { console.error("Distance error:", e.message); }
    finally { setLoadingDist(false); }
  }, []);

  const handleSelectShift = useCallback((shift) => {
    setSelectedShift(shift);
    setSelectedClient(null);
    setDistances({});
    const client = clients.find(c => c.id === shift.clientId)
                || clients.find(c => c.fullName === shift.clientName);
    if (client?.address) {
      const roleWorkers = shift.role ? workers.filter(w => w.role === shift.role) : workers;
      computeDistances(client.address, roleWorkers);
    }
  }, [clients, workers, computeDistances]);

  const handleSelectClient = useCallback((client) => {
    setSelectedClient(client);
    setSelectedShift(null);
    setDistances({});
    if (client?.address) computeDistances(client.address, workers);
  }, [workers, computeDistances]);

  // Employees tab: distances FROM one employee TO all clients (the inverse of the
  // dispatch flow). /api/distances takes origin + list of addresses, so we pass the
  // employee's address as the origin and all clients as the targets; results come
  // back keyed by client id.
  const computeEmployeeDistances = useCallback(async (empAddress) => {
    if (!empAddress || !clients.length) return;
    setLoadingClientDist(true);
    try {
      const results = await fetchDistances(empAddress, clients);
      const map = {};
      results.forEach(r => { map[r.workerId] = { miles: r.distanceMiles, minutes: r.durationMinutes }; });
      setClientDistances(map);
    } catch (e) { console.error("Employee distance error:", e.message); }
    finally { setLoadingClientDist(false); }
  }, [clients]);

  const handleSelectEmployee = useCallback((employee) => {
    setSelectedEmployee(employee);
    setSelectedShift(null);
    setSelectedClient(null);
    setClientDistances({});
    if (employee?.address) computeEmployeeDistances(employee.address);
  }, [computeEmployeeDistances]);

  // Force a data refresh: clear the backend caches (which also re-warms shifts
  // in the background) and re-pull the latest users, locations, and staff into
  // the current view so edits made in Teambridge show up without a page reload.
  const refreshData = useCallback(async () => {
    setRefreshing(true);
    setRefreshDone(false);
    // Clear backend caches and kick off a full re-warm (shifts included).
    await fetch(`${RAILWAY}/api/admin/refresh`).catch(() => {});
    // Fast lists — users, locations, staff — update the view right away.
    try {
      const [c, w, st] = await Promise.all([
        fetchClients(),
        fetchWorkers(),
        fetch(`${RAILWAY}/api/staff`).then(r => r.json()).then(d => d.staff || []).catch(() => []),
      ]);
      setClients(c);
      setWorkers(w);
      setStaff(st);
    } catch (e) {
      console.error("Refresh failed:", e.message);
    }
    setRefreshing(false);
    setRefreshDone(true);
    setTimeout(() => setRefreshDone(false), 5000);
    // Shifts + schedule are the large pull (~15k-row table). Refresh them in the
    // background with the sidebar's loading state so the button stays responsive;
    // a cold pull can take a minute or two, then the new shifts appear.
    setLoadingData(true);
    try {
      const [s, sched] = await Promise.all([
        fetchOpenShifts(),
        fetch(`${RAILWAY}/api/schedule/week`).then(r => r.json()),
      ]);
      setShifts(s);
      setWeeklySchedule(sched.byWorker || {});
      setAssignments(sched.assignments || {});
      setWeekStart(sched.weekStart || null);
      setWeekEnd(sched.weekEnd || null);
    } catch (e) {
      console.error("Shift refresh failed:", e.message);
    } finally {
      setLoadingData(false);
    }
  }, []);

  const activeClient = selectedShift
    ? (clients.find(c => c.id === selectedShift.clientId)
    || clients.find(c => c.fullName === selectedShift.clientName))
    : selectedClient;

  const activeAddress = activeClient?.address || null;
  const activeRole    = selectedShift?.role   || null;

  // Clients with a Date of Birth on file (e.g. Richard Blakeney) join the
  // birthday tracker so we remember to reach out, just like staff. Client names
  // are stored "Last First" (with or without a comma), so split accordingly.
  const clientBirthdays = useMemo(() => clients
    .filter(c => c.birthday)
    .map(c => {
      const parts = (c.fullName || "").replace(",", " ").split(/\s+/).filter(Boolean);
      const lastName  = parts[0] || c.fullName || "";
      const firstName = parts.slice(1).join(" ");
      return { id: c.id, firstName, lastName, birthday: c.birthday, role: "Client" };
    }), [clients]);

  // Birthdays cover caregivers PLUS internal staff (Owner/Admin/Recruitment)
  // PLUS clients with a DOB on file; dispatch/proximity keeps using `workers`.
  const birthdayPeople = useMemo(
    () => [...workers, ...staff, ...clientBirthdays],
    [workers, staff, clientBirthdays]);

  const filteredShifts = roleFilter === "all"
    ? shifts
    : shifts.filter(s => s.role === roleFilter);

  function toIdArray(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(v => typeof v === "object" ? v?.id || String(v) : String(v));
    if (typeof val === "object") return [val?.id || String(val)];
    return [String(val)];
  }

  function isWorkerDNR(worker, client) {
    if (!client) return false;
    return toIdArray(client.dnrUsers).includes(worker.id) ||
           toIdArray(worker.dnrLocations).includes(client.id);
  }

  function isWorkerPreferred(worker, client) {
    if (!client) return false;
    return toIdArray(client.preferredCaregivers).includes(worker.id);
  }

  function matchesRole(worker) {
    if (roleFilter !== "all") return worker.role === roleFilter;
    if (activeRole) return worker.role === activeRole;
    return true;
  }

  function matchesShiftPref(worker) {
    return shiftPrefFilter === "all" || (worker.shiftPref || []).includes(shiftPrefFilter);
  }

  const filtersActive = roleFilter !== "all" || shiftPrefFilter !== "all";
  const clearFilters = () => { setRoleFilter("all"); setShiftPrefFilter("all"); };

  // When a specific shift is selected, flag caregivers who already have a shift
  // overlapping that shift's time window (a double-booking). Returns the
  // conflicting shift, or null.
  function scheduleConflict(worker) {
    if (!selectedShift?.scheduledStart || !selectedShift?.scheduledEnd) return null;
    const start = new Date(selectedShift.scheduledStart).getTime();
    const end   = new Date(selectedShift.scheduledEnd).getTime();
    const sched = assignments?.[worker.id] || [];
    for (const sh of sched) {
      if (!sh.start || !sh.end || sh.id === selectedShift.id) continue;
      const s = new Date(sh.start).getTime();
      const e = new Date(sh.end).getTime();
      if (s < end && e > start) return { start: sh.start, end: sh.end, clientName: sh.clientName };
    }
    return null;
  }

  const filteredWorkers = workers
    .filter(w => matchesRole(w) && matchesShiftPref(w) && !isWorkerDNR(w, activeClient))
    .map(w => ({
      ...w,
      ...distances[w.id],
      isPreferred: isWorkerPreferred(w, activeClient),
      isDNR: false,
      conflict: scheduleConflict(w),
    }))
    .sort((a, b) => {
      // Booked caregivers sink below available ones (but stay above DNR).
      if (!!a.conflict !== !!b.conflict) return a.conflict ? 1 : -1;
      if (a.isPreferred !== b.isPreferred) return a.isPreferred ? -1 : 1;
      return (parseFloat(a.miles) || 9999) - (parseFloat(b.miles) || 9999);
    });

  const dnrWorkers = workers
    .filter(w => matchesRole(w) && matchesShiftPref(w) && isWorkerDNR(w, activeClient))
    .map(w => ({ ...w, ...distances[w.id], isPreferred: false, isDNR: true }));

  if (!isAuthenticated) return <LoginScreen />;

  return (
    <div className="app-shell">
      <BirthdayAlert workers={birthdayPeople} />
      {/* ── Banner ── */}
      <div className="app-banner">
        <img src={banner} alt="NursePartners Proximity Dispatch Tool" className="banner-img" />
        <div className="banner-user">
          {loadingDist && (
            <span className="loading-pill"><span className="spinner" /> Computing…</span>
          )}
          <button
            className="refresh-btn"
            onClick={refreshData}
            disabled={refreshing}
            title="Re-pull the latest from Teambridge. Users, locations and staff update immediately; shifts refresh in the background (may take a minute).">
            {refreshing ? <><span className="spinner" /> Refreshing…</> : refreshDone ? "✓ Refreshed" : "↻ Refresh data"}
          </button>
          <span className="user-chip">{user?.name || user?.username}</span>
        </div>
      </div>

      {/* ── Main Tabs ── */}
      <div className="main-tabs">
        <button
          className={`main-tab ${mainTab === "dispatch" ? "active" : ""}`}
          onClick={() => setMainTab("dispatch")}>
          📋 Scheduler Dispatch
        </button>
        <button
          className={`main-tab ${mainTab === "applicants" ? "active" : ""}`}
          onClick={() => setMainTab("applicants")}>
          👤 Applicant Proximity
        </button>
        <button
          className={`main-tab ${mainTab === "onboarding" ? "active" : ""}`}
          onClick={() => setMainTab("onboarding")}>
          📈 Onboarding &amp; Retention
        </button>
        <button
          className={`main-tab ${mainTab === "reports" ? "active" : ""}`}
          onClick={() => setMainTab("reports")}>
          📄 PBJ Reports
        </button>
        <button
          className={`main-tab ${mainTab === "chat" ? "active" : ""}`}
          onClick={() => setMainTab("chat")}>
          💬 Chat
        </button>
        <button
          className={`main-tab ${mainTab === "interview" ? "active" : ""}`}
          onClick={() => setMainTab("interview")}>
          📅 Interview Calendar
        </button>
      </div>

      {/* ── Scheduler Dispatch ── */}
      {mainTab === "dispatch" && (
        <div className="app-body">
          <Sidebar
            mode={mode} setMode={setMode}
            shifts={filteredShifts} clients={clients}
            workers={workers} weeklySchedule={weeklySchedule}
            selectedShift={selectedShift} selectedClient={selectedClient} selectedEmployee={selectedEmployee}
            onSelectShift={handleSelectShift} onSelectClient={handleSelectClient} onSelectEmployee={handleSelectEmployee}
            roleFilter={roleFilter} setRoleFilter={setRoleFilter}
            shiftPrefFilter={shiftPrefFilter} setShiftPrefFilter={setShiftPrefFilter}
            filtersActive={filtersActive} onClearFilters={clearFilters}
            loading={loadingData} />

          <main className="main-panel">
            {error && <div className="error-banner">⚠ {error}</div>}
            {mode === "employees" ? (
              <EmployeePanel
                employee={selectedEmployee}
                clients={clients}
                shifts={shifts}
                distances={clientDistances}
                loadingDist={loadingClientDist}
                onComputeDistances={computeEmployeeDistances}
                schedule={selectedEmployee ? weeklySchedule?.[selectedEmployee.id] : null}
                weekStart={weekStart}
                weekEnd={weekEnd}
                isDNR={isWorkerDNR}
                isPreferred={isWorkerPreferred} />
            ) : (
              <>
                <div className="main-toolbar">
                  <div className="toolbar-left">
                    {activeClient ? (
                      <div className="active-context">
                        <span className="context-label">{mode === "shifts" ? "Shift at" : "Client"}</span>
                        <span className="context-name">{activeClient.fullName}</span>
                        {activeAddress && <span className="context-address">{activeAddress}</span>}
                      </div>
                    ) : (
                      <span className="context-placeholder">
                        ← Select a {mode === "shifts" ? "shift" : "client"} to see nearby workers
                      </span>
                    )}
                  </div>
                  <div className="toolbar-filters">
                    <select className="filter-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                      <option value="all">All roles</option>
                      <option value="CNA">CNA only</option>
                      <option value="LPN">LPN only</option>
                      <option value="RN">RN only</option>
                    </select>
                    <select className="filter-select" value={shiftPrefFilter} onChange={e => setShiftPrefFilter(e.target.value)}>
                      <option value="all">Any shift pref.</option>
                      {SHIFT_PREFS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    {filtersActive && (
                      <button className="filter-clear" onClick={clearFilters} title="Clear both filters">✕ Clear</button>
                    )}
                  </div>
                </div>
                <WorkerList
                  workers={filteredWorkers}
                  dnrWorkers={dnrWorkers}
                  loading={loadingDist || loadingData}
                  hasSelection={!!activeClient}
                  activeClient={activeClient}
                  weeklySchedule={weeklySchedule}
                  weekStart={weekStart}
                  weekEnd={weekEnd} />
              </>
            )}
          </main>

          <BirthdaysPanel workers={birthdayPeople} />
        </div>
      )}

      {/* ── Applicant Proximity ── */}
      {mainTab === "applicants" && (
        <div className="app-body">
          <ApplicantTab shifts={shifts} clients={clients} />
        </div>
      )}

      {/* ── Onboarding & Retention ── */}
      {mainTab === "onboarding" && (
        <div className="app-body">
          <OnboardingRetention />
        </div>
      )}

      {/* ── PBJ Reports ── */}
      {mainTab === "reports" && (
        <div className="app-body">
          <PbjReport />
        </div>
      )}

      {/* ── Chat ── */}
      {mainTab === "chat" && (
        <div className="app-body">
          <ChatTab />
        </div>
      )}

      {/* ── Interview Calendar ── */}
      {mainTab === "interview" && (
        <div className="app-body">
          <InterviewCalendar />
        </div>
      )}
    </div>
  );
}
